import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Analytics as AnalyticsIcon,
  Article as ArticleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface AdminAnalyticsProps {}

interface AnalyticsData {
  userGrowth: Array<{ month: string; users: number; growth: number }>;
  revenueData: Array<{ month: string; revenue: number; growth: number }>;
  prStats: Array<{ category: string; submissions: number; published: number; rate: number }>;
  topChannels: Array<{ name: string; submissions: number; published: number; rate: number }>;
}

interface Metrics {
  totalUsers: number;
  userGrowthPercent: number;
  monthlyRevenue: number;
  revenueGrowthPercent: number;
  totalPRs: number;
  prGrowthPercent: number;
  publishedPRs: number;
  publishedGrowthPercent: number;
}

const AdminAnalytics: React.FC<AdminAnalyticsProps> = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<Metrics>({
    totalUsers: 0,
    userGrowthPercent: 0,
    monthlyRevenue: 0,
    revenueGrowthPercent: 0,
    totalPRs: 0,
    prGrowthPercent: 0,
    publishedPRs: 0,
    publishedGrowthPercent: 0
  });
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    userGrowth: [],
    revenueData: [],
    prStats: [],
    topChannels: []
  });

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/admin/analytics`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          timeRange
        }
      });

      if (response.data.success) {
        setMetrics(response.data.metrics || {
          totalUsers: 0,
          userGrowthPercent: 0,
          monthlyRevenue: 0,
          revenueGrowthPercent: 0,
          totalPRs: 0,
          prGrowthPercent: 0,
          publishedPRs: 0,
          publishedGrowthPercent: 0
        });
        setAnalyticsData({
          userGrowth: response.data.userGrowth || [],
          revenueData: response.data.revenueData || [],
          prStats: response.data.prStats || [],
          topChannels: response.data.topChannels || []
        });
      }
    } catch (err: any) {
      console.error('Error fetching analytics data:', err);
      setError(err.response?.data?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const getGrowthIcon = (growth: number) => {
    return growth > 0 ? (
      <TrendingUpIcon style={{ color: '#4caf50', fontSize: 16 }} />
    ) : (
      <TrendingDownIcon style={{ color: '#f44336', fontSize: 16 }} />
    );
  };

  const getGrowthColor = (growth: number) => {
    return growth > 0 ? '#4caf50' : '#f44336';
  };

  if (loading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" style={{ marginBottom: '24px' }}>
          {error}
        </Alert>
        <Box style={{ display: 'flex', gap: '16px' }}>
          <button
            onClick={fetchAnalyticsData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#0927eb',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" style={{ fontWeight: 'bold' }}>
          Analytics Dashboard
        </Typography>
        <Box style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <FormControl style={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              label="Time Range"
            >
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
              <MenuItem value="1y">Last year</MenuItem>
            </Select>
          </FormControl>
          <button
            onClick={fetchAnalyticsData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#0927eb',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <RefreshIcon style={{ fontSize: 18 }} />
            Refresh
          </button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Key Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h4">
                    {metrics.totalUsers.toLocaleString()}
                  </Typography>
                  <Box style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                    {getGrowthIcon(metrics.userGrowthPercent)}
                    <Typography variant="body2" style={{ color: getGrowthColor(metrics.userGrowthPercent), marginLeft: '4px' }}>
                      {metrics.userGrowthPercent > 0 ? '+' : ''}{metrics.userGrowthPercent.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
                <PeopleIcon style={{ fontSize: 40, color: '#0927eb' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Monthly Revenue
                  </Typography>
                  <Typography variant="h4">
                    ${metrics.monthlyRevenue.toLocaleString()}
                  </Typography>
                  <Box style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                    {getGrowthIcon(metrics.revenueGrowthPercent)}
                    <Typography variant="body2" style={{ color: getGrowthColor(metrics.revenueGrowthPercent), marginLeft: '4px' }}>
                      {metrics.revenueGrowthPercent > 0 ? '+' : ''}{metrics.revenueGrowthPercent.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
                <MoneyIcon style={{ fontSize: 40, color: '#ff6b35' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    PR Submissions
                  </Typography>
                  <Typography variant="h4">
                    {metrics.totalPRs.toLocaleString()}
                  </Typography>
                  <Box style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                    {getGrowthIcon(metrics.prGrowthPercent)}
                    <Typography variant="body2" style={{ color: getGrowthColor(metrics.prGrowthPercent), marginLeft: '4px' }}>
                      {metrics.prGrowthPercent > 0 ? '+' : ''}{metrics.prGrowthPercent.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
                <ArticleIcon style={{ fontSize: 40, color: '#0927eb' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Published PRs
                  </Typography>
                  <Typography variant="h4">
                    {metrics.publishedPRs.toLocaleString()}
                  </Typography>
                  <Box style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                    {getGrowthIcon(metrics.publishedGrowthPercent)}
                    <Typography variant="body2" style={{ color: getGrowthColor(metrics.publishedGrowthPercent), marginLeft: '4px' }}>
                      {metrics.publishedGrowthPercent > 0 ? '+' : ''}{metrics.publishedGrowthPercent.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
                <AnalyticsIcon style={{ fontSize: 40, color: '#4caf50' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* PR Statistics by Category */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                PR Statistics by Industry
              </Typography>
              {analyticsData.prStats.length === 0 ? (
                <Box style={{ padding: '40px', textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    No PR statistics available
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} style={{ marginTop: '16px' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Industry</TableCell>
                        <TableCell align="right">Submissions</TableCell>
                        <TableCell align="right">Published</TableCell>
                        <TableCell align="right">Success Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.prStats.map((stat, index) => (
                        <TableRow key={index}>
                          <TableCell>{stat.category}</TableCell>
                          <TableCell align="right">{stat.submissions.toLocaleString()}</TableCell>
                          <TableCell align="right">{stat.published.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={`${stat.rate.toFixed(1)}%`} 
                              color={stat.rate > 90 ? 'success' : stat.rate > 85 ? 'primary' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Publishing Channels */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Publishing Channels
              </Typography>
              {analyticsData.topChannels.length === 0 ? (
                <Box style={{ padding: '40px', textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    No channel statistics available
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} style={{ marginTop: '16px' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Channel</TableCell>
                        <TableCell align="right">Submissions</TableCell>
                        <TableCell align="right">Published</TableCell>
                        <TableCell align="right">Success Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.topChannels.map((channel, index) => (
                        <TableRow key={index}>
                          <TableCell>{channel.name}</TableCell>
                          <TableCell align="right">{channel.submissions.toLocaleString()}</TableCell>
                          <TableCell align="right">{channel.published.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={`${channel.rate.toFixed(1)}%`} 
                              color={channel.rate > 93 ? 'success' : 'primary'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Growth Chart Placeholder */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue Growth Trend
              </Typography>
              {analyticsData.revenueData.length === 0 ? (
                <Box style={{ 
                  height: '300px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  marginTop: '16px'
                }}>
                  <Typography variant="body1" color="textSecondary">
                    No revenue data available for the selected time range
                  </Typography>
                </Box>
              ) : (
                <Box style={{ 
                  height: '300px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  marginTop: '16px',
                  padding: '20px'
                }}>
                  <Box style={{ width: '100%' }}>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                      Revenue by Month:
                    </Typography>
                    {analyticsData.revenueData.map((item, index) => (
                      <Box key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: index < analyticsData.revenueData.length - 1 ? '1px solid #e0e0e0' : 'none'
                      }}>
                        <Typography variant="body2">{item.month}</Typography>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                            ${item.revenue.toLocaleString()}
                          </Typography>
                          <Box style={{ display: 'flex', alignItems: 'center' }}>
                            {getGrowthIcon(item.growth)}
                            <Typography variant="caption" style={{ color: getGrowthColor(item.growth), marginLeft: '4px' }}>
                              {item.growth > 0 ? '+' : ''}{item.growth.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* User Growth Chart Placeholder */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Growth Trend
              </Typography>
              {analyticsData.userGrowth.length === 0 ? (
                <Box style={{ 
                  height: '300px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  marginTop: '16px'
                }}>
                  <Typography variant="body1" color="textSecondary">
                    No user growth data available for the selected time range
                  </Typography>
                </Box>
              ) : (
                <Box style={{ 
                  height: '300px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  marginTop: '16px',
                  padding: '20px'
                }}>
                  <Box style={{ width: '100%' }}>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
                      Users by Month:
                    </Typography>
                    {analyticsData.userGrowth.map((item, index) => (
                      <Box key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: index < analyticsData.userGrowth.length - 1 ? '1px solid #e0e0e0' : 'none'
                      }}>
                        <Typography variant="body2">{item.month}</Typography>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                            {item.users.toLocaleString()} users
                          </Typography>
                          <Box style={{ display: 'flex', alignItems: 'center' }}>
                            {getGrowthIcon(item.growth)}
                            <Typography variant="caption" style={{ color: getGrowthColor(item.growth), marginLeft: '4px' }}>
                              {item.growth > 0 ? '+' : ''}{item.growth.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminAnalytics;
